import type ChatService from './Chat';
import type { Chat } from '../types';
import { ChatProtocol, ChatType } from '../types/enums';
import config from '../config';
import ChatUtils from './ChatUtils';
import Utils from '../Utils';

enum NonzaName {
  ENABLE = 'enable',
  ENABLED = 'enabled',
  REQUEST = 'r',
  ANSWER = 'a',
}

export default class StreamManagement {
  readonly xmlns = 'urn:xmpp:sm:3';

  private enabled: boolean = false;
  private clientHandledCounter: number = 0;
  private serverHandledCounter: number = 0;
  private messagesQueue: Set<Chat.MessageParams> = new Set();
  private messagesQueueClearTimer?: NodeJS.Timeout | number;

  constructor(private chat: ChatService) {}

  public get supported(): boolean {
    return config.chatProtocol.active === ChatProtocol.WS && config.chat.streamManagement?.enable;
  }

  public start(): void {
    this.enabled = false;

    if (this.supported) {
      this.removeElementHandler();
      this.addElementHandler();
      this.sendNonza(NonzaName.ENABLE);
    }
  }

  public stop(): void {
    this.enabled = false;

    if (this.supported) {
      this.removeElementHandler();
      this.markMessagesQueueAsLost();
    }
  }

  public async sendAndCount(stanza: Chat.XmlElement, message: Chat.MessageParams): Promise<void> {
    const type = ChatUtils.getAttr(stanza, 'type');
    const body = ChatUtils.getElementText(stanza, 'body');
    const attachments = ChatUtils.getAllElements(stanza, 'attachment');
    const isMessage = stanza.name === 'message';
    const isChat = type === ChatType.CHAT || type === ChatType.GROUPCHAT;
    const isContent = Boolean(body || attachments.length);

    try {
      await this.chat.xmppClient.sendOnline(stanza);

      if (this.enabled && isMessage && isChat && isContent) {
        this.messagesQueue.add(message);
        this.sendNonza(NonzaName.REQUEST);
      }
    } catch {
      this.markMessageAsLost(message);
    }
  }

  private elementHandler = (stanza: Chat.XmlElement): void => {
    const [name, xmlns] = [stanza.name, ChatUtils.getAttr(stanza, 'xmlns')];

    if (xmlns === this.xmlns && name === NonzaName.ENABLED) {
      this.clientHandledCounter = 0;
      this.serverHandledCounter = 0;
      this.enabled = true;
      return;
    }

    if (xmlns !== this.xmlns) this.clientHandledCounter++;

    if (name === NonzaName.REQUEST) {
      this.sendNonza(NonzaName.ANSWER);
      return;
    }

    if (name === NonzaName.ANSWER) {
      const nextHandledCounter = parseInt(ChatUtils.getAttr(stanza, 'h'), 10);
      const currHandledCounter = Number.isNaN(nextHandledCounter) ? this.serverHandledCounter : nextHandledCounter;
      const diffHandledCounter = currHandledCounter - this.serverHandledCounter;

      this.serverHandledCounter = currHandledCounter;
      this.markMessagesQueueAsSent(diffHandledCounter);
      this.markMessagesQueueAsLostByTimeout();
    }
  };

  private markMessageAsSent(message: Chat.MessageParams): void {
    Utils.safeCallbackCall(this.chat.onSentMessageCallback)(null, message);
    this.messagesQueue.delete(message);
  }

  private markMessageAsLost(message: Chat.MessageParams, clear: boolean = true): void {
    Utils.safeCallbackCall(this.chat.onSentMessageCallback)(message, null);
    if (clear) this.messagesQueue.delete(message);
  }

  private markMessagesQueueAsSent(iterations: number = 0): void {
    const iterator = this.messagesQueue.values();

    for (let i = 0; i < iterations; i++) {
      const { done, value } = iterator.next();
      if (done) break;
      this.markMessageAsSent(value);
    }
  }

  private markMessagesQueueAsLost(): void {
    clearTimeout(this.messagesQueueClearTimer);
    this.messagesQueueClearTimer = undefined;

    if (this.messagesQueue.size > 0) {
      this.messagesQueue.forEach((message) => {
        this.markMessageAsLost(message, false);
      });
      this.messagesQueue.clear();
    }
  }

  private markMessagesQueueAsLostByTimeout(): void {
    const ackTimeout = (config.chat.streamManagement?.acknowledgementTimeout ?? 30) * 1000;

    clearTimeout(this.messagesQueueClearTimer);
    this.messagesQueueClearTimer = setTimeout(() => {
      this.markMessagesQueueAsLost();
    }, ackTimeout);
  }

  private sendNonza(name: string): Promise<void> {
    const attrs = { xmlns: this.xmlns, ...(name === NonzaName.ANSWER && { h: this.clientHandledCounter }) };
    const nonza = ChatUtils.createNonza(name, attrs);

    return this.chat.xmppClient.sendOnline(nonza);
  }

  private removeElementHandler(): void {
    this.chat.xmppClient.removeListener('element', this.elementHandler);
  }

  private addElementHandler(): void {
    this.chat.xmppClient.on('element', this.elementHandler);
  }
}
